import React from "react";
import MarkdownIt from "markdown-it";
import { BenchmarkRun } from "@/lib/api/resources/benchmark";
import { Alert, Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";

const md = new MarkdownIt({
  html: false,
  linkify: true,
  typographer: true,
});

interface RunAdviceProps {
  run: BenchmarkRun;
}

export const RunAdvice: React.FC<RunAdviceProps> = ({ run }) => {
  // Downloads the raw Markdown advice text as a file on the client-side
  const handleExport = () => {
    if (!run.advice) return;

    // Create a text/markdown blob from the advice string
    const blob = new Blob([run.advice], {
      type: "text/markdown;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);

    // Create a temporary anchor element to trigger the download
    const link = document.createElement("a");
    link.href = url;

    // Construct a safe, readable file name using the benchmark/agent name and run ID prefix
    const runId = run.id?.split("/").pop() || "run";
    const benchmarkName =
      run.benchmarkConfig?.name || run.agent?.name || "benchmark";
    const safeName = benchmarkName.toLowerCase().replace(/[^a-z0-9]+/g, "-");
    link.download = `advice-${safeName}-${runId.substring(0, 8)}.md`;

    // Programmatically click the anchor to initiate the browser download, then cleanup
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!run.advice) {
    return (
      <div className="p-8 max-w-3xl">
        <Alert
          message="No Judge Advice Available"
          description="There is no advice generated for this run. Advice is automatically generated when a run completes and judging is enabled."
          type="info"
          showIcon
        />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl bg-gray-50/30 rounded-xl border border-gray-100">
      <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100">
        <h3 className="text-lg font-semibold text-slate-800 m-0">
          Judge Analysis & Advice
        </h3>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleExport}
          className="bg-blue-600 hover:bg-blue-500 border-blue-600 hover:border-blue-500"
        >
          Export to Markdown
        </Button>
      </div>
      <div
        className="text-slate-800 space-y-4 leading-relaxed
        [&>h1]:text-2xl [&>h1]:font-bold [&>h1]:mt-6 [&>h1]:mb-4
        [&>h2]:text-xl [&>h2]:font-bold [&>h2]:mt-6 [&>h2]:mb-3
        [&>h3]:text-lg [&>h3]:font-bold [&>h3]:mt-5 [&>h3]:mb-2
        [&>p]:my-4
        [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:my-4 [&>ul>li]:mb-1
        [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:my-4 [&>ol>li]:mb-1
        [&>pre]:bg-gray-800 [&>pre]:text-gray-100 [&>pre]:p-4 [&>pre]:rounded-lg [&>pre]:overflow-x-auto [&>pre]:my-4
        [&>code]:bg-gray-100 [&>code]:text-pink-600 [&>code]:px-1.5 [&>code]:py-0.5 [&>code]:rounded [&>code]:text-sm
        [&>blockquote]:border-l-4 [&>blockquote]:border-gray-300 [&>blockquote]:pl-4 [&>blockquote]:italic [&>blockquote]:my-4 [&>blockquote]:text-gray-600
        [&>a]:text-blue-600 [&>a]:underline [&>a]:hover:text-blue-800"
        dangerouslySetInnerHTML={{ __html: md.render(run.advice) }}
      />
    </div>
  );
};
